import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { navItems } from "@/lib/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getPeriodNavigationSearch } from "@/lib/periodNavigation";
import { Logo } from "@/components/molecules/Logo";

const STORAGE_KEY = "admin-sidebar-collapsed";

interface SidebarProps {
  mobileOpen: boolean;
  onCloseMobile: () => void;
  returnFocusRef?: RefObject<HTMLElement>;
}

export function Sidebar({ mobileOpen, onCloseMobile, returnFocusRef }: SidebarProps) {
  const location = useRouterState({ select: (state) => state.location });
  const pathname = location.pathname;
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => window.localStorage.getItem(STORAGE_KEY) === "true");
  const desktopSidebarRef = useRef<HTMLElement>(null);
  const mobileDrawerRef = useRef<HTMLElement>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseMobileRef = useRef(onCloseMobile);
  const periodSearch = getPeriodNavigationSearch((location.search as Record<string, unknown>) ?? {});

  useEffect(() => {
    onCloseMobileRef.current = onCloseMobile;
  }, [onCloseMobile]);

  useLayoutEffect(() => {
    desktopSidebarRef.current?.toggleAttribute("inert", mobileOpen);
    mobileDrawerRef.current?.toggleAttribute("inert", !mobileOpen);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;

    restoreFocusRef.current =
      returnFocusRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    mobileCloseButtonRef.current?.focus();

    function handleMobileDrawerKeyDown(event: KeyboardEvent) {
      const drawer = mobileDrawerRef.current;
      if (!drawer) return;

      if (event.key === "Escape") {
        event.preventDefault();
        onCloseMobileRef.current();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.getAttribute("aria-hidden") !== "true");

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || !drawer.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeElement === last || !drawer.contains(activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleMobileDrawerKeyDown);

    return () => {
      document.removeEventListener("keydown", handleMobileDrawerKeyDown);
      const elementToRestore = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (elementToRestore?.isConnected) elementToRestore.focus();
    };
  }, [mobileOpen, returnFocusRef]);

  const groupedItems = useMemo(
    () => ({
      dashboards: navItems.filter((item) => item.group === "dashboards"),
      gestao: navItems.filter((item) => item.group === "gestao"),
      whatsapp: navItems.filter((item) => item.group === "whatsapp"),
      sistema: navItems.filter((item) => item.group === "sistema"),
    }),
    []
  );

  function toggleCollapsed() {
    setCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }

  return (
    <>
      <aside
        ref={desktopSidebarRef}
        className={cn(
          "hidden h-screen shrink-0 border-r bg-sidebar text-sidebar-foreground transition-all duration-300 lg:flex lg:flex-col",
          collapsed ? "w-14" : "w-44"
        )}
        aria-hidden={mobileOpen ? "true" : undefined}
      >
        <div className="flex h-14 items-center justify-center border-b border-sidebar-border px-2">
          <Logo showText={!collapsed} />
        </div>

        <nav
          className="flex-1 space-y-4 overflow-y-auto px-1.5 py-3"
          aria-label="Navegação principal desktop"
        >
          <SidebarGroup
            collapsed={collapsed}
            label="Dashboards"
            items={groupedItems.dashboards}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
            }}
          />
          <SidebarGroup
            collapsed={collapsed}
            label="Gestao"
            items={groupedItems.gestao}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
            }}
          />
          <SidebarGroup
            collapsed={collapsed}
            label="WhatsApp"
            items={groupedItems.whatsapp}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
            }}
          />
          <SidebarGroup
            collapsed={collapsed}
            label="Sistema"
            items={groupedItems.sistema}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
            }}
          />
        </nav>

        <div className="border-t p-1.5">
          <Button variant="ghost" size="sm" className="w-full justify-center" onClick={toggleCollapsed}>
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!collapsed && <span className="ml-1.5">Colapsar</span>}
          </Button>
        </div>
      </aside>

      {/* z-index: mobileOverlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 touch-none overscroll-contain bg-black/40 transition-opacity lg:hidden",
          mobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onCloseMobile}
        aria-hidden="true"
      />
      {/* z-index: mobileSidebar */}
      <aside
        ref={mobileDrawerRef}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-dvh max-h-dvh w-60 flex-col overflow-hidden border-r bg-sidebar text-sidebar-foreground transition-transform duration-300 lg:hidden",
          mobileOpen ? "pointer-events-auto translate-x-0" : "pointer-events-none -translate-x-full"
        )}
        role="dialog"
        aria-modal={mobileOpen ? "true" : undefined}
        aria-hidden={!mobileOpen}
        aria-label="Menu principal mobile"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-sidebar-border px-4">
          <Logo />
          <Button
            ref={mobileCloseButtonRef}
            variant="ghost"
            size="icon"
            onClick={onCloseMobile}
            aria-label="Fechar menu"
          >
            <X size={18} aria-hidden="true" />
          </Button>
        </div>

        <nav
          className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-1.5 py-3"
          aria-label="Navegação principal mobile"
        >
          <SidebarGroup
            collapsed={false}
            label="Dashboards"
            items={groupedItems.dashboards}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
              onCloseMobile();
            }}
          />
          <SidebarGroup
            collapsed={false}
            label="Gestao"
            items={groupedItems.gestao}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
              onCloseMobile();
            }}
          />
          <SidebarGroup
            collapsed={false}
            label="WhatsApp"
            items={groupedItems.whatsapp}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
              onCloseMobile();
            }}
          />
          <SidebarGroup
            collapsed={false}
            label="Sistema"
            items={groupedItems.sistema}
            pathname={pathname}
            onSelect={(path) => {
              navigate({ to: path, search: periodSearch as never }).catch(() => undefined);
              onCloseMobile();
            }}
          />
        </nav>
      </aside>
    </>
  );
}

interface SidebarGroupProps {
  collapsed: boolean;
  label: string;
  items: typeof navItems;
  pathname: string;
  onSelect: (path: string) => void;
}

function SidebarGroup({ collapsed, label, items, pathname, onSelect }: SidebarGroupProps) {
  return (
    <div>
      {!collapsed && <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>}
      <ul className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          const active =
            item.path === "/users"
              ? pathname === "/users"
              : item.path.startsWith("/users/")
                ? pathname.startsWith("/users/")
                : pathname === item.path;

          return (
            <li key={item.path}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                  active ? "bg-sidebar-primary text-sidebar-primary-foreground" : "hover:bg-sidebar-accent text-sidebar-foreground"
                )}
                title={item.title}
                onClick={() => onSelect(item.path)}
              >
                <Icon size={18} aria-hidden="true" />
                {!collapsed && <span className="ml-2">{item.title}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
